import React, { useState, useMemo, useEffect } from 'react';
import { Box, Card, CardContent, Typography, Divider, CircularProgress, Alert, Button, FormControl, Select, MenuItem, InputLabel, Chip, OutlinedInput, IconButton, Checkbox, ListItemText, Slider, Tooltip, useTheme, useMediaQuery, ToggleButtonGroup, ToggleButton, alpha } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ClearIcon from '@mui/icons-material/Clear';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import SankeyChart from './SankeyChart';
import DonutChart from './DonutChart';
import { ChevronRight } from '@mui/icons-material';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const MoneyFlowSection = ({ data, formatCurrency, categories, startMonth, endMonth, setStartMonth, setEndMonth }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [topN, setTopN] = useState(null);
  const [focusedCategory, setFocusedCategory] = useState(null);
  const [chartType, setChartType] = useState('sankey');

  // Set default topN based on actual category count, capped sensibly
  useEffect(() => {
    if (topN === null && data && data.nodes) {
      const categoryCount = data.nodes.filter(n =>
        (n.category === 'expenses' || n.category === 'income') && n.name !== 'Total Income'
      ).length;
      setTopN(isMobile ? Math.min(categoryCount, 5) : Math.min(categoryCount, 8));
    }
  }, [data, isMobile, topN]);

  function ValueLabelComponent(props) {
    const { children, value } = props;
    return (
      <Tooltip enterTouchDelay={0} placement="bottom" title={value}>
        {children}
      </Tooltip>
    );
  }

  const processedData = useMemo(() => {
    if (!data || !data.nodes || !data.links) {
      return { nodes: [], links: [] };
    }

    const reindex = (nodes, links) => {
        const nodeIndexMap = new Map(nodes.map((node, i) => [node.id, i]));
        const reindexedLinks = links.map(link => ({
          ...link,
          source: nodeIndexMap.get(link.source),
          target: nodeIndexMap.get(link.target),
        })).filter(l => l.source !== undefined && l.target !== undefined);
        return { nodes, links: reindexedLinks };
    };

    const categoryNodes = data.nodes.filter(n =>
      (n.category === 'expenses' || n.category === 'income') && n.name !== 'Total Income'
    );
    
    categoryNodes.sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
    
    const otherCategoryNodes = categoryNodes.slice(topN);

    if (otherCategoryNodes.length === 0) {
      return reindex(data.nodes, data.links);
    }

    const otherIncomeNodes = otherCategoryNodes.filter(n => n.category === 'income');
    const otherExpenseNodes = otherCategoryNodes.filter(n => n.category === 'expenses');
    
    const otherIncomeIds = new Set(otherIncomeNodes.map(n => n.id));
    const otherExpenseIds = new Set(otherExpenseNodes.map(n => n.id));
    const allOtherIds = new Set([...otherIncomeIds, ...otherExpenseIds]);

    const newNodes = data.nodes.filter(n => !allOtherIds.has(n.id));
    
    if (otherIncomeNodes.length > 0) {
      const otherIncomeValue = otherIncomeNodes.reduce((sum, n) => sum + n.value, 0);
      newNodes.push({ id: 'other-income-group', name: 'Other Income', value: otherIncomeValue, category: 'income' });
    }
    if (otherExpenseNodes.length > 0) {
      const otherExpenseValue = otherExpenseNodes.reduce((sum, n) => sum + n.value, 0);
      newNodes.push({ id: 'other-expense-group', name: 'Other Expenses', value: otherExpenseValue, category: 'expenses' });
    }

    const otherLinksAggregator = new Map();
    const finalLinks = [];

    data.links.forEach(link => {
      const isSourceOtherIncome = otherIncomeIds.has(link.source);
      const isTargetOtherExpense = otherExpenseIds.has(link.target);
      const value = typeof link.value === 'number' ? link.value : 0;

      if (isSourceOtherIncome && isTargetOtherExpense) {
        const key = 'other-income-group___other-expense-group';
        if (!otherLinksAggregator.has(key)) {
          otherLinksAggregator.set(key, { source: 'other-income-group', target: 'other-expense-group', value: 0 });
        }
        otherLinksAggregator.get(key).value += value;
      } else if (isSourceOtherIncome) {
        const key = `other-income-group___${link.target}`;
        if (!otherLinksAggregator.has(key)) {
          otherLinksAggregator.set(key, { source: 'other-income-group', target: link.target, value: 0 });
        }
        otherLinksAggregator.get(key).value += value;
      } else if (isTargetOtherExpense) {
        const key = `${link.source}___other-expense-group`;
        if (!otherLinksAggregator.has(key)) {
          otherLinksAggregator.set(key, { source: link.source, target: 'other-expense-group', value: 0 });
        }
        otherLinksAggregator.get(key).value += value;
      } else {
        finalLinks.push({ ...link });
      }
    });

    finalLinks.push(...otherLinksAggregator.values());
    
    return reindex(newNodes, finalLinks);

  }, [data, topN]);

  const colorMap = {};
  categories.forEach(cat => {
    if (cat.name) colorMap[cat.name] = cat.color;
    if (cat.id) colorMap[cat.id] = cat.color;
  });
  const fallbackColors = {
    income: '#10B981',
    expenses: '#EF4444',
    savings: '#3B82F6',
    general: '#6366F1',
    other: '#A3A3A3',
    netBalance: '#3B82F6',
  };

  const legendNodes = processedData?.nodes?.filter(
    n => n.name !== 'Total Income'
  ) || [];
  const legendEntries = Array.from(
    new Map(
      legendNodes.map(n => [n.name, n])
    ).values()
  );

  const legendColor = (node) => {
    if (node.name === 'Other') return fallbackColors.other;
    if (node.name === 'Net Balance') return fallbackColors.netBalance;
    if (node.name === 'Total Income') return fallbackColors.general;
    return colorMap[node.name] || fallbackColors[node.category] || fallbackColors.other;
  };

  // Shared legend: one pill chip per node (matches the trends chart /
  // Projected Balances legend chips). Click focuses/unfocuses the category.
  const renderLegend = (entries) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 0.75, mt: 1 }}>
      {entries.map((node) => {
        const color = legendColor(node);
        const isFocused = focusedCategory === node.name;
        const dimmed = Boolean(focusedCategory) && !isFocused;
        return (
          <Chip
            key={node.name}
            size="small"
            variant={dimmed ? 'outlined' : 'filled'}
            onClick={() => setFocusedCategory(isFocused ? null : node.name)}
            title={isFocused ? 'Clear focus' : `Focus on ${node.name}`}
            icon={
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  ml: '6px !important',
                  borderRadius: '50%',
                  bgcolor: dimmed ? 'grey.400' : color,
                }}
              />
            }
            label={node.name}
            sx={{
              height: 24,
              fontSize: '0.75rem',
              fontWeight: isFocused ? 600 : 400,
              bgcolor: dimmed ? 'transparent' : alpha(color, 0.12),
              color: dimmed ? 'text.secondary' : 'text.primary',
              '&:hover': { bgcolor: alpha(color, 0.2) },
            }}
          />
        );
      })}
    </Box>
  );

  const hasData = processedData?.nodes?.length > 0;
  const hasLinks = processedData?.links?.length > 0;
  const hasNoDataError = !hasData || !hasLinks;
  
  const maxTopN = useMemo(() => {
    if (!data || !data.nodes) return 5;
    const categoryNodes = data.nodes.filter(n =>
        (n.category === 'expenses' || n.category === 'income') && n.name !== 'Total Income'
    );
    return categoryNodes.length > 1 ? categoryNodes.length : 5;
  }, [data]);

  return (
    <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box component="span" sx={{ display: 'inline-flex', color: 'text.secondary', '& svg': { fontSize: '1.1rem' } }}>
            <AccountTreeIcon />
          </Box>
          {chartType === 'sankey' ? 'Money Flow' : 'Income & Expense Breakdown'}
        </Typography>
        <ToggleButtonGroup
          size="small"
          value={chartType}
          exclusive
          onChange={(_, val) => { if (val) setChartType(val); }}
          aria-label="Chart type"
          sx={{ mr: 0.5, '& .MuiToggleButton-root': { px: 0.5, py: 0.25, minHeight: 28 } }}
        >
          <ToggleButton value="sankey" aria-label="Sankey flow chart">
            <AccountTreeIcon sx={{ fontSize: 16 }} />
          </ToggleButton>
          <ToggleButton value="donut" aria-label="Donut charts">
            <DonutLargeIcon sx={{ fontSize: 16 }} />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Divider />
  <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'row',
          flexWrap: 'wrap',
      gap: 2, 
      mb: 1,
          alignItems: 'center',
          fontSize: 14,
        }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <DatePicker
                label="Start Month"
                views={['year', 'month']}
                maxDate={endMonth}
                value={startMonth}
                onChange={val => setStartMonth(val)}
                format="MMM-yy"
                slotProps={{
                  textField: {
                    size: 'small',
                  },
                }}
              />
              <DatePicker
                label="End Month"
                views={['year', 'month']}
                minDate={startMonth}
                value={endMonth}
                onChange={val => setEndMonth(val)}
                format="MMM-yy"
                slotProps={{ 
                  textField: { 
                    size: 'small'
                  } 
                }}
              />
            </Box>
          </LocalizationProvider>
          <Box sx={{
            width: { xs: 100, md: 100 },
            height: 35,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            border: '1px solid rgba(0, 0, 0, 0.23)',
            borderRadius: 1,
            px: 2,
            '&:hover': {
                borderColor: 'text.primary',
            },
          }}>
            <Typography component="label" id="top-n-slider-label" sx={{
              position: 'absolute',
              top: -10,
              left: 9,
              zIndex: 1,
              px: 0.5,
              bgcolor: 'white',
              fontSize: { xs: '0.75rem', sm: 'small' },
              color: 'text.secondary',
            }}>
              # Categories
            </Typography>
            <Slider
              aria-labelledby="top-n-slider-label"
              value={topN}
              onChange={(e, newValue) => setTopN(newValue)}
              step={1}
              marks
              min={1}
              max={maxTopN}
              valueLabelDisplay="auto"
              size="small"
              slots={{ valueLabel: ValueLabelComponent }}
            />
          </Box>
        </Box>
  <Box sx={{ bgcolor: 'grey.50', py: 0.5, borderRadius: 1, mb: 0.5, position: 'relative', minHeight: 380, width: '100%' }}>
          {hasNoDataError ? (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              minHeight: 400
            }}>
              <Typography color="text.secondary">
                No data available for the selected date range and filters
              </Typography>
            </Box>
          ) : chartType === 'sankey' ? (
            <SankeyChart 
              data={processedData} 
              formatCurrency={formatCurrency} 
              height={400} 
              categories={categories}
              focusedCategory={focusedCategory}
            />
      ) : (
            <DonutChart 
              data={processedData} 
              formatCurrency={formatCurrency} 
              height={isMobile ? 480 : 380} 
              categories={categories}
              focusedCategory={focusedCategory}
            />
          )}
        </Box>
        {!hasNoDataError && chartType === 'sankey' && renderLegend(legendEntries)}

        {!hasNoDataError && chartType === 'donut' && renderLegend([
          ...(processedData?.nodes || []).filter(n => n.category === 'income' && n.name !== 'Total Income'),
          ...(processedData?.nodes || []).filter(n => n.category === 'expenses'),
        ])}
      </CardContent>
    </Card>
  );
};

export default MoneyFlowSection;